import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { staffClient } from 'clients/staff';
import './style.scss';

moment.locale('ko');
const localizer = momentLocalizer(moment);

interface Event {
    id: number;
    title: string;
    description?: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    type?: string;
    color?: string;
    creator?: any;
}

interface CreateEventForm {
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    all_day: boolean;
    type: string;
    color: string;
}

const CalendarPage: React.FC = () => {
    const [events, setEvents] = useState<Event[]>([]);
    const [todayEvents, setTodayEvents] = useState<Event[]>([]);
    const [view, setView] = useState<View>('month');
    const [date, setDate] = useState(new Date());
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<any>(null);
    const [createForm, setCreateForm] = useState<CreateEventForm>({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        all_day: false,
        type: 'meeting',
        color: '#3b82f6'
    });

    useEffect(() => {
        fetchEvents();
        fetchTodayEvents();
    }, [date, view]);

    const fetchEvents = async () => {
        try {
            let response;
            if (view === 'month') {
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                response = await staffClient.get(`/event/month?year=${year}&month=${month}`);
            } else {
                response = await staffClient.get('/event');
            }
            
            const eventsData = response.data.map((event: any) => ({
                ...event,
                start: new Date(event.start_date),
                end: new Date(event.end_date),
            }));
            
            setEvents(eventsData);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        }
    };

    const fetchTodayEvents = async () => {
        try {
            const response = await staffClient.get('/event/today');
            const eventsData = response.data.map((event: any) => ({
                ...event,
                start: new Date(event.start_date),
                end: new Date(event.end_date),
            }));
            setTodayEvents(eventsData);
        } catch (error) {
            console.error('Failed to fetch today events:', error);
        }
    };

    const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
        setSelectedSlot({ start, end });
        setCreateForm({
            ...createForm,
            start_date: moment(start).format('YYYY-MM-DDTHH:mm'),
            end_date: moment(end).format('YYYY-MM-DDTHH:mm')
        });
        setShowCreateModal(true);
    };

    const handleSelectEvent = (event: Event) => {
        if (window.confirm('이 일정을 삭제하시겠습니까?')) {
            deleteEvent(event.id);
        }
    };

    const createEvent = async () => {
        try {
            await staffClient.post('/event', createForm);
            setShowCreateModal(false);
            setCreateForm({
                title: '',
                description: '',
                start_date: '',
                end_date: '',
                all_day: false,
                type: 'meeting',
                color: '#3b82f6'
            });
            fetchEvents();
            fetchTodayEvents();
        } catch (error) {
            console.error('Failed to create event:', error);
            alert('일정 생성에 실패했습니다.');
        }
    };

    const deleteEvent = async (eventId: number) => {
        try {
            await staffClient.delete(`/event/${eventId}`);
            fetchEvents();
            fetchTodayEvents();
        } catch (error) {
            console.error('Failed to delete event:', error);
            alert('일정 삭제에 실패했습니다.');
        }
    };

    const eventStyleGetter = (event: Event) => {
        const getEventTypeColor = (type: string) => {
            switch (type) {
                case 'work': return '#22c55e';
                case 'meeting': return '#f59e0b';
                case 'vacation': return '#ec4899';
                case 'holiday': return '#ef4444';
                case 'personal': return '#8b5cf6';
                default: return event.color || '#3b82f6';
            }
        };

        const backgroundColor = getEventTypeColor(event.type || 'meeting');
        
        return {
            style: {
                backgroundColor,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                padding: '2px 6px',
                margin: '1px 0',
                fontWeight: '500'
            }
        };
    };

    return (
        <div className="calendar-page">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <div className="header-content">
                    <div>
                        <h1 className="page-title">캘린더</h1>
                        <p className="page-subtitle">일정 관리 및 회사 행사 확인</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn btn-secondary">
                            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            내보내기
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={() => {
                                setSelectedSlot({ start: new Date(), end: new Date() });
                                setCreateForm({
                                    ...createForm,
                                    start_date: moment().format('YYYY-MM-DDTHH:mm'),
                                    end_date: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm')
                                });
                                setShowCreateModal(true);
                            }}
                        >
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"/>
                            </svg>
                            일정 추가
                        </button>
                    </div>
                </div>
            </div>

            <div className="calendar-layout">
                {/* 캘린더 메인 */}
                <div className="calendar-main">
                    <div className="calendar-container">
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: 'calc(100vh - 240px)' }}
                            onSelectSlot={handleSelectSlot}
                            onSelectEvent={handleSelectEvent}
                            selectable
                            popup
                            view={view}
                            onView={setView}
                            date={date}
                            onNavigate={setDate}
                            eventPropGetter={eventStyleGetter}
                            messages={{
                                next: '다음',
                                previous: '이전',
                                today: '오늘',
                                month: '월',
                                week: '주',
                                day: '일',
                                agenda: '일정',
                                date: '날짜',
                                time: '시간',
                                event: '일정',
                                noEventsInRange: '해당 기간에 일정이 없습니다.',
                                showMore: (total) => `+${total} 더보기`
                            }}
                        />
                    </div>
                </div>

                {/* 사이드 패널 */}
                <div className="calendar-sidebar">
                    {/* 미니 캘린더 */}
                    <div className="sidebar-card">
                        <div className="mini-calendar-header">
                            <button 
                                className="nav-btn"
                                onClick={() => setDate(moment(date).subtract(1, 'month').toDate())}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                </svg>
                            </button>
                            <h4 className="mini-calendar-title">
                                {moment(date).format('YYYY년 M월')}
                            </h4>
                            <button 
                                className="nav-btn"
                                onClick={() => setDate(moment(date).add(1, 'month').toDate())}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </div>
                        
                        <div className="mini-calendar">
                            <div className="weekdays">
                                {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                                    <div key={day} className="weekday">{day}</div>
                                ))}
                            </div>
                            
                            <div className="mini-calendar-grid">
                                {/* 미니 캘린더 날짜들 - 실제로는 동적으로 생성해야 함 */}
                                {Array.from({length: 42}, (_, i) => {
                                    const startOfMonth = moment(date).startOf('month');
                                    const startOfCalendar = startOfMonth.clone().startOf('week');
                                    const currentDate = startOfCalendar.clone().add(i, 'days');
                                    const isToday = currentDate.isSame(moment(), 'day');
                                    const isCurrentMonth = currentDate.isSame(date, 'month');
                                    
                                    return (
                                        <div 
                                            key={i} 
                                            className={`mini-day ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                                            onClick={() => setDate(currentDate.toDate())}
                                        >
                                            {currentDate.date()}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* 오늘의 일정 */}
                    <div className="sidebar-card">
                        <h3 className="sidebar-title">오늘의 일정</h3>
                        <div className="today-events">
                            {todayEvents.length > 0 ? (
                                todayEvents.map(event => (
                                    <div key={event.id} className="today-event">
                                        <div className={`event-dot ${event.type || 'meeting'}`}></div>
                                        <div className="event-info">
                                            <div className="event-title">{event.title}</div>
                                            <div className="event-time">
                                                {event.allDay ? '종일' : moment(event.start).format('HH:mm')}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-events">오늘 일정이 없습니다.</div>
                            )}
                        </div>
                    </div>

                    {/* 일정 유형별 범례 */}
                    <div className="sidebar-card">
                        <h3 className="sidebar-title">일정 유형</h3>
                        <div className="event-legend">
                            <div className="legend-item">
                                <div className="legend-dot work"></div>
                                <span>업무</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-dot meeting"></div>
                                <span>회의</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-dot vacation"></div>
                                <span>휴가</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-dot holiday"></div>
                                <span>공휴일</span>
                            </div>
                            <div className="legend-item">
                                <div className="legend-dot personal"></div>
                                <span>개인</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 일정 생성 모달 */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>새 일정</h3>
                            <button 
                                className="modal-close"
                                onClick={() => setShowCreateModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>제목</label>
                                <input
                                    type="text"
                                    value={createForm.title}
                                    onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                                    placeholder="일정 제목을 입력하세요"
                                />
                            </div>
                            <div className="form-group">
                                <label>설명</label>
                                <textarea
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                                    placeholder="일정 설명을 입력하세요"
                                    rows={3}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>시작일시</label>
                                    <input
                                        type={createForm.all_day ? "date" : "datetime-local"}
                                        value={createForm.all_day ? 
                                            createForm.start_date.split('T')[0] : 
                                            createForm.start_date
                                        }
                                        onChange={(e) => {
                                            const value = createForm.all_day ? 
                                                `${e.target.value}T00:00` : 
                                                e.target.value;
                                            setCreateForm({...createForm, start_date: value});
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>종료일시</label>
                                    <input
                                        type={createForm.all_day ? "date" : "datetime-local"}
                                        value={createForm.all_day ? 
                                            createForm.end_date.split('T')[0] : 
                                            createForm.end_date
                                        }
                                        onChange={(e) => {
                                            const value = createForm.all_day ? 
                                                `${e.target.value}T23:59` : 
                                                e.target.value;
                                            setCreateForm({...createForm, end_date: value});
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group checkbox-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={createForm.all_day}
                                            onChange={(e) => {
                                                const isAllDay = e.target.checked;
                                                setCreateForm({
                                                    ...createForm, 
                                                    all_day: isAllDay,
                                                    start_date: isAllDay ? 
                                                        createForm.start_date.split('T')[0] + 'T00:00' : 
                                                        createForm.start_date,
                                                    end_date: isAllDay ? 
                                                        createForm.end_date.split('T')[0] + 'T23:59' : 
                                                        createForm.end_date
                                                });
                                            }}
                                        />
                                        <span className="checkmark"></span>
                                        종일
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>타입</label>
                                    <select
                                        value={createForm.type}
                                        onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                                    >
                                        <option value="meeting">회의</option>
                                        <option value="work">업무</option>
                                        <option value="vacation">휴가</option>
                                        <option value="holiday">공휴일</option>
                                        <option value="personal">개인</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>색상</label>
                                <div className="color-picker">
                                    {[
                                        { value: '#3b82f6', label: '파랑', color: '#3b82f6' },
                                        { value: '#22c55e', label: '초록', color: '#22c55e' },
                                        { value: '#f59e0b', label: '노랑', color: '#f59e0b' },
                                        { value: '#ec4899', label: '분홍', color: '#ec4899' },
                                        { value: '#ef4444', label: '빨강', color: '#ef4444' },
                                        { value: '#8b5cf6', label: '보라', color: '#8b5cf6' }
                                    ].map(colorOption => (
                                        <div 
                                            key={colorOption.value}
                                            className={`color-option ${createForm.color === colorOption.value ? 'selected' : ''}`}
                                            onClick={() => setCreateForm({...createForm, color: colorOption.value})}
                                        >
                                            <div 
                                                className="color-circle" 
                                                style={{ backgroundColor: colorOption.color }}
                                            ></div>
                                            <span>{colorOption.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setShowCreateModal(false)}
                            >
                                취소
                            </button>
                            <button 
                                className="btn btn-primary"
                                onClick={createEvent}
                                disabled={!createForm.title}
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarPage;
